import { API_BASE_URL } from "../lib/backendUrl";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard,
  Radar,
  Building2,
  Calendar,
  Package,
  Users,
  Settings,
  LogOut,
  ChevronLeft,
  Menu,
  User,
  FileCheck,
  CalendarDays,
  UserCircle,
  Target,
  Briefcase,
  Compass,
  Mail,
  MessageSquare,
  Network,
  ChevronDown,
  ChevronRight,
  X,
} from "lucide-react";
import NotificationsDropdown from "./NotificationsDropdown";
import OperatorAssistant from "./OperatorAssistant";
import ErrorBoundary from "./workflow/ErrorBoundary";
import BetaFeedbackButton from "./workflow/BetaFeedbackButton";
import { isSubcontractor } from "../lib/roles";
import {
  PRIMARY_NAVIGATION,
  UTILITY_NAVIGATION,
  activeNavigation,
  routeMatches,
  visiblePrimaryNavigation,
} from "../features/navigation/navigationModel";

const NAV_ICONS = {
  dashboard: LayoutDashboard,
  projects: Building2,
  pursuits: Target,
  operations: Radar,
  network: Network,
  communications: MessageSquare,
  decisions: FileCheck,
  "calendar-days": CalendarDays,
  discover: Compass,
  pipeline: Target,
  schedule: Calendar,
  "control-tower": Radar,
  procurement: Package,
  marketplace: Briefcase,
  workforce: Users,
  companies: Briefcase,
  contacts: UserCircle,
  messages: MessageSquare,
  email: Mail,
  settings: Settings,
};

const Layout = ({ children }) => {
  const { user, tenant, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Below `lg` the sidebar is an off-canvas drawer rather than a fixed rail.
  // A construction PM opens these screens from a phone on site, so the shell
  // cannot reserve 288px of gutter at every width.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const externalUser = isSubcontractor(user);
  const primaryNavigation = visiblePrimaryNavigation(externalUser);
  const active = activeNavigation(location.pathname, primaryNavigation);
  const [expandedGroupId, setExpandedGroupId] = useState(
    () => activeNavigation(location.pathname, PRIMARY_NAVIGATION)?.groupId || null
  );

  useEffect(() => {
    const nextPrimaryNavigation = visiblePrimaryNavigation(externalUser);
    const nextActive = activeNavigation(location.pathname, nextPrimaryNavigation);
    if (nextActive && nextPrimaryNavigation.some((entry) => entry.id === nextActive.groupId && entry.children)) {
      setExpandedGroupId(nextActive.groupId);
    }
  }, [location.pathname, externalUser]);

  useEffect(() => {
    const fetchMessageUnread = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/messages/unread-count`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const data = await response.json();
        setMessageUnreadCount(data.count || 0);
      } catch {
        // ignore
      }
    };
    fetchMessageUnread();
    const interval = setInterval(fetchMessageUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  // A navigation click on a phone must close the drawer it came from.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Below lg the closed drawer is only translated off-screen, so without this
  // it would still hold ~17 links in the tab order — the first Tab press on
  // every page would land in something invisible. `inert` removes it from
  // both the tab order and the accessibility tree; it is applied through the
  // DOM because React 19 is the first version to treat it as a real prop and
  // the attribute must be absent (not `inert="false"`) when the drawer is open.
  const drawerRef = useRef(null);
  useEffect(() => {
    const node = drawerRef.current;
    if (!node) return undefined;
    const apply = () => {
      const offCanvas = !mobileNavOpen && window.matchMedia("(max-width: 1023px)").matches;
      if (offCanvas) {
        node.setAttribute("inert", "");
        node.setAttribute("aria-hidden", "true");
      } else {
        node.removeAttribute("inert");
        node.removeAttribute("aria-hidden");
      }
    };
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, [mobileNavOpen]);

  // Escape closes the drawer, the way every other overlay in the app behaves.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileNavOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleGroup = (groupId) => {
    if (sidebarCollapsed) setSidebarCollapsed(false);
    setExpandedGroupId((current) => (current === groupId ? null : groupId));
  };

  const navLink = (item, child = false) => {
    const Icon = NAV_ICONS[item.icon];
    const isActive = routeMatches(location.pathname, item.path);
    const isMessages = item.path === "/messages";
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setMobileNavOpen(false)}
        aria-current={isActive ? "page" : undefined}
        aria-label={sidebarCollapsed ? item.label : undefined}
        title={sidebarCollapsed ? item.label : undefined}
        className={`sidebar-item ${child ? "ml-4 text-sm" : ""} ${
          isActive ? "sidebar-item-active" : ""
        } ${sidebarCollapsed ? "justify-center px-2" : ""}`}
        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        <Icon size={child ? 18 : 20} aria-hidden="true" />
        {!sidebarCollapsed && <span>{item.label}</span>}
        {!sidebarCollapsed && isMessages && messageUnreadCount > 0 && (
          <span
            className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-white"
            aria-label={`${messageUnreadCount} unread messages`}
          >
            {messageUnreadCount > 99 ? "99+" : messageUnreadCount}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Scrim behind the mobile drawer */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — off-canvas drawer below lg, fixed rail at lg and above */}
      <aside
        ref={drawerRef}
        className={`fixed left-0 top-0 h-full w-72 bg-[#0F1016] border-r border-white/5 transition-transform duration-300 z-50 lg:transition-all ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 ${sidebarCollapsed ? "lg:w-20" : "lg:w-72"}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="py-5 px-4 border-b border-white/5">
            {!sidebarCollapsed ? (
              <div className="flex items-center justify-between">
                <Link to="/dashboard" className="block flex-1">
                  <img
                    src="https://customer-assets.emergentagent.com/job_omnas-construction/artifacts/01xhlqjp_Wide%20Logo%20Omnas.png"
                    alt="OMNΛS"
                    className="w-full max-w-[200px] h-auto"
                  />
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground flex-shrink-0 ml-2 hidden lg:inline-flex"
                  data-testid="sidebar-toggle"
                  aria-label="Collapse navigation"
                >
                  <ChevronLeft size={18} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileNavOpen(false)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground flex-shrink-0 ml-2 lg:hidden"
                  aria-label="Close navigation"
                >
                  <X size={18} />
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Link to="/dashboard">
                  <img
                    src="https://customer-assets.emergentagent.com/job_build-flow-6/artifacts/69xrzvvn_1000012166%20%282%29.png"
                    alt="OMNΛS"
                    className="h-10 w-auto"
                  />
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground mt-2"
                  data-testid="sidebar-toggle"
                  aria-label="Expand navigation"
                >
                  <Menu size={18} />
                </Button>
              </div>
            )}
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-2" aria-label="Primary navigation">
              {primaryNavigation.map((entry) => {
                if (entry.path) return navLink(entry);

                const Icon = NAV_ICONS[entry.icon];
                const isExpanded = expandedGroupId === entry.id && !sidebarCollapsed;
                const isActive = active?.groupId === entry.id;
                return (
                  <div key={entry.id} data-testid={`nav-group-${entry.id}`}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(entry.id)}
                      aria-expanded={isExpanded}
                      aria-controls={`nav-children-${entry.id}`}
                      aria-label={sidebarCollapsed ? entry.label : undefined}
                      title={sidebarCollapsed ? entry.label : undefined}
                      data-active={isActive ? "true" : undefined}
                      className={`sidebar-item w-full ${isActive ? "sidebar-item-active" : ""} ${
                        sidebarCollapsed ? "justify-center px-2" : ""
                      }`}
                      data-testid={`nav-domain-${entry.id}`}
                    >
                      <Icon size={20} aria-hidden="true" />
                      {!sidebarCollapsed && (
                        <>
                          <span>{entry.label}</span>
                          {isActive && <span className="sr-only">Contains current page</span>}
                          {isExpanded ? (
                            <ChevronDown className="ml-auto" size={16} aria-hidden="true" />
                          ) : (
                            <ChevronRight className="ml-auto" size={16} aria-hidden="true" />
                          )}
                        </>
                      )}
                    </button>
                    {isExpanded && (
                      <div id={`nav-children-${entry.id}`} className="mt-1 space-y-1">
                        {entry.children.map((item) => navLink(item, true))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="my-3 border-t border-white/5" aria-hidden="true" />
              {UTILITY_NAVIGATION.map((item) => navLink(item))}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="border-t border-white/5 p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors ${
                    sidebarCollapsed ? "justify-center" : ""
                  }`}
                  data-testid="user-menu-trigger"
                >
                  <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                    {user?.full_name
                      ?.split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase() || "U"}
                  </div>
                  {!sidebarCollapsed && (
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium truncate">
                        {user?.full_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate("/settings")}
                  className="cursor-pointer"
                >
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/settings")}
                  className="cursor-pointer"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                  data-testid="logout-btn"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content — no gutter below lg, where the sidebar is off-canvas */}
      <main
        className={`flex-1 min-w-0 transition-all duration-300 ml-0 ${
          sidebarCollapsed ? "lg:ml-20" : "lg:ml-72"
        }`}
      >
        {/* Top Header */}
        <header className="h-16 border-b border-border/50 bg-background/80 backdrop-blur-lg sticky top-0 z-30">
          <div className="h-full flex items-center justify-between px-4 sm:px-6 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 lg:hidden shrink-0"
                aria-label="Open navigation"
                aria-expanded={mobileNavOpen}
                onClick={() => {
                  // A drawer is never the icon-only rail — open it expanded.
                  setSidebarCollapsed(false);
                  setMobileNavOpen(true);
                }}
              >
                <Menu size={18} />
              </Button>
              <h2 className="text-sm text-muted-foreground truncate">
                {tenant?.name || "Organization"}
              </h2>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              <BetaFeedbackButton />
              <NotificationsDropdown />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6 md:p-8">
          <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
        </div>
      </main>
      <OperatorAssistant />
    </div>
  );
};

export default Layout;
